import type { PopoverProps } from '@mui/material';
import { Popover, Stack, TextField } from '@mui/material';

import { tss } from 'tss-react/mui';

import { useState } from 'react';

import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';
import { getFileDisplayName, getFileExtension } from '@/utils/file/fileUtils';
import { useMutation } from '@/hooks/mutationWithSnackbar';
import { Or2Button } from '@/components/button/Or2Button';
import { Or2DialogProgressBar } from '@/components/feedback/Or2DialogProgressBar';

const CHANGE_TEXT_POPOVER_FRAGMENT = graphql(`
  fragment changeTextPopoverFields on File {
    ...fileMiniFields
  }
`);

interface Props extends PopoverProps {
  file: DocumentType<typeof CHANGE_TEXT_POPOVER_FRAGMENT>;
  onClose: () => void;
}

const RENAME_FILE_MUTATION = graphql(`
  mutation RenameFile($data: RenameFileInput!) {
    renameFile(params: $data) {
      file {
        ...fileFields
      }
    }
  }
`);

const useStyles = tss.create(({ theme }) => ({
  box: {
    padding: theme.spacing(1),
    boxSizing: 'border-box',
    minWidth: '24em',
    overflow: 'hidden',
  },
}));

export const ChangeTextPopover = ({
  file,
  anchorEl,
  anchorOrigin = {
    vertical: -16,
    horizontal: -24,
  },
  transformOrigin = {
    vertical: 'top',
    horizontal: 'left',
  },
  onClose,
  ...other
}: Props) => {
  const fileDisplayName = getFileDisplayName(file);
  const [filename, setFilename] = useState(fileDisplayName ?? '');
  const { classes } = useStyles();

  const [renameFile, { loading }] = useMutation(RENAME_FILE_MUTATION, {
    completionMessage: `${file.folder ? 'Folder' : 'File'} renamed to ${filename}`,
    onCompleted: onClose,
  });

  const fileExtension = getFileExtension(file) || '';

  const doRename = () => {
    if (fileDisplayName === filename) {
      return onClose();
    }

    renameFile({
      variables: {
        data: {
          file: file._id,
          filename: filename + fileExtension,
        },
      },
    });
  };

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      onClick={(event) => event.stopPropagation()}
      onClose={onClose}
      {...other}
    >
      <div className={classes.box}>
        <Stack spacing={1} direction="row" sx={{ alignItems: 'center' }}>
          <TextField
            fullWidth
            autoFocus
            value={filename}
            size="small"
            onChange={(event) => setFilename(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && doRename()}
          />
          <Or2Button onClick={doRename}>OK</Or2Button>
        </Stack>
      </div>
      <Or2DialogProgressBar loading={loading} />
    </Popover>
  );
};
